import Alert from "features/shared/alerts/components/Alert";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React from "react";
import { useHistory } from "react-router-dom";
import InsertRequirementResultDTO from "features/requirements/dtos/InsertRequirementResultDTO";
import { useTranslation } from "react-i18next";
import {
  getRequirementsListPath,
  resolvePostCreateRoute,
} from "features/requirements/helpers/requirementModuleNavigation";

interface Props {
  responseStatus: number;
  result?: InsertRequirementResultDTO;
  successMessage?: string;
  errorMessage?: string;
  moreInfoMessage?: string;
  renderActionBtn?: () => JSX.Element;
  renderTransitionBtn?: () => JSX.Element;
  showRequirementNavigation?: boolean;
}

const CreateRequirementResultSubDialog = ({
  responseStatus,
  result,
  successMessage,
  errorMessage,
  moreInfoMessage,
  renderActionBtn,
  renderTransitionBtn,
  showRequirementNavigation = false,
}: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const dialogService = useInjection<IContentDialogService>(
    IocTypes.ContentDialogService
  );

  // router
  const history = useHistory();

  // helpers
  const isSuccessStatus = (): boolean => {
    return responseStatus < 300;
  };

  const hasValidationMessages = (): boolean => {
    return (result?.validationMessages?.length ?? 0) > 0;
  };

  const canRenderAutomaticNavigation = (): boolean => {
    return (
      showRequirementNavigation &&
      isSuccessStatus() &&
      !hasValidationMessages()
    );
  };

  // handlers
  const closeDialogHandler = (route?: string) => {
    if (route) {
      history.push(route);
    }

    dialogService.closeDialog();
  };

  // sub-renders
  const renderMoreInfo = (): JSX.Element | undefined => {
    if (moreInfoMessage) {
      return (
        <Alert
          alertObj={{
            alertType: "Info",
            message: moreInfoMessage,
          }}
          showIconOnMobile={false}
          className="mx-0 mb-0 mt-4"
        />
      );
    }
  };

  const renderErrorInfo = (): JSX.Element => {
    return (
      <Alert
        alertObj={{
          alertType: "Info",
          message: tLabels(
            "createRequirementResultSubDialog.errorMoreInfoMessage"
          ),
        }}
        showIconOnMobile={false}
        className="mx-0 mb-0 mt-4"
      />
    );
  };

  const renderInfo = (): JSX.Element => {
    if (!isSuccessStatus() || hasValidationMessages()) {
      return (
        <>
          <Alert
            alertObj={{
              alertType: "Danger",
              message:
                errorMessage ??
                tLabels(
                  "createRequirementResultSubDialog.errorMessage"
                ),
            }}
            className="m-0"
          />

          {renderErrorInfo()}
        </>
      );
    } else {
      return (
        <>
          <Alert
            alertObj={{
              alertType: "Success",
              message:
                successMessage ??
                tLabels(
                  "createRequirementResultSubDialog.successMessage"
                ),
            }}
            className="m-0"
          />

          {renderMoreInfo()}
        </>
      );
    }
  };

  const renderActionButton = () => {
    if (!isSuccessStatus() || hasValidationMessages()) {
      return (
        <button
          type="button"
          className="btn btn-primary ml-2"
          onClick={() => closeDialogHandler()}
        >
          {tLabels(
            "createRequirementResultSubDialog.checkForm"
          )}
        </button>
      );
    } else if (renderActionBtn) {
      return renderActionBtn();
    } else if (canRenderAutomaticNavigation()) {
      const detailRoute = resolvePostCreateRoute(result);

      if (!detailRoute) {
        return undefined;
      }

      return (
        <button
          type="button"
          className="btn btn-primary ml-2"
          onClick={() => closeDialogHandler(detailRoute)}
        >
          {tLabels(
            "createRequirementResultSubDialog.requirementDetail"
          )}
        </button>
      );
    }
  };

  const renderTransitionButton = () => {
    if (renderTransitionBtn) {
      return renderTransitionBtn();
    } else if (!canRenderAutomaticNavigation()) {
      if (isSuccessStatus() && !hasValidationMessages()) {
        return (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => closeDialogHandler("/")}
          >
            {tLabels("dynamicRequirementFormSection.mainPage")}
          </button>
        );
      }

      return undefined;
    } else {
      return (
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() =>
            closeDialogHandler(getRequirementsListPath())
          }
        >
          {tLabels(
            "createRequirementResultSubDialog.rquirementsList"
          )}
        </button>
      );
    }
  };

  // render
  return (
    <>
      <div className="p-4">
        {renderInfo()}
      </div>
      <div className="d-flex justify-content-end px-4 pb-4">
        {renderTransitionButton()}
        {renderActionButton()}
      </div>
    </>
  );
};

export default CreateRequirementResultSubDialog;